import { type MarketRef, useMarketDirectory } from './company';
import { regionFixtures, useFixtureModule } from './fixtures';
import type { LiveQuery, Region, RegionKind } from './types';

/**
 * Which `/regions/<slug>` pages exist, and what they are called. One answer for every surface that
 * links to a region — the tracked pages on Home, news cards and rows, activity rows — so none of
 * them keeps a parallel list that drifts from the route.
 *
 * The region route (`routes/_protected/regions/$slug.tsx`) renders a page for a slug when either
 * the fixture regions hold it (markets such as `ontario`, states, the roll-up and the continents —
 * a continent exists only there) or the warehouse does (`markets.get`, which the shared markets
 * directory, `markets.list`, enumerates). The union of those two is therefore exactly the set of
 * slugs that do not render "Region not found".
 */

/** @desc One region page: enough to name it, tile it and link it. */
export type RegionPageRef = {
  slug: string;
  /** @desc The page's own display name ("Asia Pacific"), as its follow button names it. */
  name: string;
  kind: RegionKind;
  /** @desc The directory eyebrow: the continent a market belongs to; "Continent" on a continent. */
  eyebrow: string;
  /** @desc An `EntityLogo` flag code (`gb`, `us-nj`), when the warehouse knows the market's ISO code. */
  flagCode?: string;
  /** @desc The letters the tile falls back to, from the fixture region. */
  code?: string;
};

export type RegionPages = {
  /** @desc The page for `slug`, or `undefined` when the portal has none. */
  get: (slug: string) => RegionPageRef | undefined;
};

/**
 * @desc The region pages that exist: every live market from the shared directory, then the fixture
 * regions the warehouse does not hold. A live market keeps its live name and flag; the fixture
 * only adds its tile letters.
 */
export const resolveRegionPages = (
  fixtures: readonly Region[],
  markets: ReadonlyMap<string, MarketRef>,
): RegionPages => {
  const fixtureBySlug = new Map(fixtures.map((region) => [region.slug, region]));
  const pages = new Map<string, RegionPageRef>();

  for (const [slug, market] of markets) {
    const fixture = fixtureBySlug.get(slug);
    pages.set(slug, {
      slug,
      name: market.name,
      kind: fixture?.kind ?? 'market',
      eyebrow: market.continent.name,
      ...(market.flagCode ? { flagCode: market.flagCode } : {}),
      ...(fixture?.code ? { code: fixture.code } : {}),
    });
  }

  for (const fixture of fixtures) {
    if (pages.has(fixture.slug)) continue;
    const continent = fixture.continentSlug ? fixtureBySlug.get(fixture.continentSlug) : undefined;
    pages.set(fixture.slug, {
      slug: fixture.slug,
      name: fixture.name,
      kind: fixture.kind,
      eyebrow: fixture.kind === 'continent' ? 'Continent' : (continent?.name ?? 'Region'),
      code: fixture.code,
    });
  }

  return { get: (slug) => pages.get(slug) };
};

/**
 * @desc The region pages, once both sources have answered: the lazy regions fixture (requested only
 * while `enabled`) and the shared markets directory. `data` stays `undefined` while either is still
 * loading or has failed — callers must not read that as "no such page" — and `error` carries the
 * failure for the surface that wants to show it.
 */
export const useRegionPages = (enabled = true): LiveQuery<RegionPages> => {
  const markets = useMarketDirectory();
  const fixtures = useFixtureModule(regionFixtures, enabled);
  const error =
    markets.error ?? (fixtures.error ? { message: fixtures.error.message } : null) ?? null;
  const fixtureRegions = fixtures.module?.REGIONS;
  return {
    data:
      fixtureRegions && markets.data ? resolveRegionPages(fixtureRegions, markets.data) : undefined,
    isLoading: enabled && !error && (markets.isLoading || !fixtureRegions),
    error,
  };
};
